import React from 'react';
import { ElementTemplate } from '../types/types';

// Import icons
import HeadingIcon from '../assets/icons/HeadingIcon';
import ParagraphIcon from '../assets/icons/ParagraphIcon';
import SeparatorIcon from '../assets/icons/SeparatorIcon';
import MediaIcon from '../assets/icons/MediaIcon';
import NewFormIcon from '../assets/icons/NewFormIcon';
import ButtonIcon from '../assets/icons/ButtonIcon';
import PageSearchIcon from '../assets/icons/PageSearchIcon';
import SingleColumnIcon from '../assets/icons/SingleColumnIcon';
import MultiColumnIcon from '../assets/icons/MultiColumnIcon';
import PublicDirectoryIcon from '../assets/icons/PublicDirectoryIcon';
interface ElementUIConfig {
  type: string;
  label: string;
  icon: React.ReactNode;
  category: 'basic' | 'section';
  
}

/**
 * Gets the appropriate icon component for an element type
 */
const getIconForElementType = (elementType: string): React.ReactNode => {
  const iconMap: Record<string, React.ComponentType<any>> = {
    'paragraph': ParagraphIcon,
    'heading': HeadingIcon,
    'header': HeadingIcon,
    'footer': HeadingIcon,
    'separator': SeparatorIcon,
    'media': MediaIcon,
    'image': MediaIcon,
    'video': MediaIcon,
    'form': NewFormIcon,
    'button': ButtonIcon,
    'search_query': PageSearchIcon,
    'single_column': SingleColumnIcon,
    'multi_column': MultiColumnIcon,
    'public_directory': PublicDirectoryIcon,
  };

  const IconComponent = iconMap[elementType] || SeparatorIcon;
  return React.createElement(IconComponent);
};

const categoryMap: Record<string, 'basic' | 'section'> = {
  'paragraph': 'basic',
  'heading': 'basic',
  'header': 'basic',
  'footer': 'basic',
  'separator': 'basic',
  'media': 'basic',
  'image': 'basic',
  'video': 'basic',
  'form': 'basic',
  'button': 'basic',
  'search_query': 'basic',
  'single_column': 'section',
  'multi_column': 'section',
  'public_directory': 'basic'
};

/**
 * Maps API element templates to UI element configurations with icons
 * Only processes basic elements - sections are kept static
 * @param templates - Array of element templates from API
 * @returns Array of basic element configurations
 */
export const mapTemplateToBasicElements = (templates: ElementTemplate[]): ElementUIConfig[] => {
  const basicElements: ElementUIConfig[] = [];

  templates.forEach((template) => {
    // Convert access key to type (e.g., 'search_query' stays as is, 'heading' becomes 'heading')
    const elementType = template.accessKey.toLowerCase().replace(/[_-]/g, '_');

    // Determine category - skip section elements as they're kept static
    const category = categoryMap[elementType] || 'basic';
    if (category !== 'basic') {
      return; // Skip sections, they're static
    }

    // Get icon from icon component or use a generic one
    const icon = getIconForElementType(elementType);

    const uiConfig: ElementUIConfig = {
      type: template.accessKey,
      label: template.name,
      icon: icon,
      category: 'basic',
    };

    basicElements.push(uiConfig);
  });

  return basicElements;
};

/**
 * Gets the element-specific label from API name
 * @param name - Element name from API
 * @returns Formatted label
 */
export const formatElementLabel = (name: string): string => {
  return name
    .split(/(?=[A-Z])/) // Split on capital letters
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Creates default element properties based on template
 * @param template - Element template from API
 * @returns Default properties object
 */
export const createElementFromTemplate = (template: ElementTemplate) => {
  return {
    ...template.defaultSettings,
    templateId: template.id,
    templateName: template.name,
  };
};
